<template>
  <main :class="{ loading }">
    <Header>
      <router-link
        id="go-to-settings"
        :to="{ name: 'settings' }"
        class="header__action"
      >
        <IconSettings />
        Configurações
      </router-link>
      <router-link
        :to="{ name: 'pagelist' }"
        class="header__action header__action--disabled"
      >
        Páginas
      </router-link>
      <router-link
        id="go-to-modals"
        :to="{ name: 'modallist' }"
        class="header__action"
      >
        Pop-ups
      </router-link>
      <router-link
        :to="{ name: 'metrics' }"
        class="header__action"
      >
        Métricas
      </router-link>
      <router-link
        v-if="isGlobalUser"
        id="header-back-to-admin"
        :to="{ name: 'admin' }"
        class="header__action"
      >
        Admin
      </router-link>
      <h2 class="hotel-title">
        {{ siteHostname }}
      </h2>
    </Header>
    <div class="pages">
      <div class="pages__management">
        <div class="pages__management__header">
          <h1>Lista de páginas</h1>
          <div class="no-select search">
            <IconSearch size="sm" />
            <input
              id="modelPath"
              ref="search__field"
              v-model="searchTerm"
              class="form-control search__field"
              type="text"
              placeholder="Busque por título da página, URL ou status..."
            />
          </div>
          <button
            v-if="isGlobalUser && isAdminUser"
            class="btn btn-secondary btn-icon"
            @click="importPage"
          >
            <IconUpload size="sm" />
            <span> Importar página </span>
          </button>
          <button
            class="btn btn-secondary btn-icon"
            :disabled="isViewerUser"
            @click="toggleTemplates"
          >
            <IconPlus size="sm" />
            <span> Criar página </span>
          </button>
        </div>
        <div
          v-show="showTemplates"
          class="pages__templates"
        >
          <TemplateCard
            v-for="(template, index) in templateOptions"
            :key="index"
            v-bind="template"
            :selected="
              selectedTemplate && selectedTemplate.name === template.name
            "
            :is-new="template.isNew"
            :is-updated="template.isUpdated"
            class="pages__templates__template"
            @click.native="selectTemplate(template)"
          />
        </div>
        <div
          v-if="listForTable.length === 0"
          class="pages__empty-state text-center"
        >
          <div>
            <p>
              {{
                parentPageList.length === 0
                  ? 'Você ainda não criou nenhuma página 😿'
                  : 'Nenhuma página encontrada 😿'
              }}
            </p>
          </div>
        </div>
        <CustomTable
          v-show="listForTable.length > 0"
          id="page-list"
          ref="customTable"
          class="pages__table"
          :cols="tableHeaders"
          :list="listForTable"
          :has-pagination="true"
        >
          <template #seo="{ row: page }">
            <RadialProgressBar
              :progress="page.seo"
              :title="seoTitleText(page.seo)"
            />
          </template>
          <template #title="{ row: page }">
            <div
              class="column-title"
              :class="{
                'translation-page': page.language !== 'pt',
              }"
              title="Abrir edição da página"
              @click="goToEditor(page)"
            >
              <button
                v-if="page.language === 'pt'"
                class="favorite-icon"
                :disabled="isViewerUser"
                :title="
                  !page.isFavorite ? 'Favoritar página' : 'Desfavoritar página'
                "
                @click.stop="setFavorite(page)"
              >
                <IconStar
                  size="xs"
                  :class="{
                    active: page.isFavorite,
                  }"
                />
              </button>
              <IconLanguage
                v-else
                size="xs"
                :language="page.language"
              />
              <span>{{ page.title }} </span>
            </div>
          </template>
          <template #status="{ row: page }">
            <div class="column-status">
              <span
                class="page-status"
                :title="getStatusHelpText(page)"
                :class="{
                  'page-status--draft': !page.isPublished,
                  'page-status--published':
                    page.isPublished && !isDirtyPage(page),
                  'page-status--dirty': page.isPublished && isDirtyPage(page),
                }"
              >
                {{ getPageStatus(page) }}
              </span>
            </div>
          </template>
          <template #actions="{ row: page }">
            <div class="column-actions">
              <button
                class="column-actions__cta"
                title="Abrir edição da página"
                @click="goToEditor(page)"
              >
                Editar
              </button>
              <div>
                <a
                  v-if="page.isPublished"
                  class="column-actions__cta"
                  :href="getPageAbsoluteUrl(page)"
                  target="_blank"
                  title="Abrir página em uma nova aba"
                >
                  Ver página
                </a>
                <div
                  v-else
                  class="column-actions__cta column-actions__cta--disabled"
                  title="Página não publicada"
                >
                  Ver página
                </div>
              </div>
              <button
                class="column-actions__cta-menu"
                :disabled="isViewerUser"
                @click.stop="toggleActionMenu(page.id)"
              >
                <IconDotsVert />
              </button>
              <div
                v-show="openedActionMenu === page.id"
                v-positioned
                v-clickaway="closeActionMenu"
                class="column-actions__action-menu"
              >
                <ul>
                  <li>
                    <button
                      class="column-actions__action-menu__btn column-actions__action-menu__btn--goto"
                      @click.stop="goToEditor(page)"
                    >
                      Editar página
                    </button>
                  </li>
                  <li v-if="page.language === 'pt'">
                    <button
                      class="column-actions__action-menu__btn"
                      @click.stop="setFavorite(page)"
                    >
                      {{
                        page.isFavorite
                          ? 'Desfavoritar página'
                          : 'Favoritar página'
                      }}
                    </button>
                  </li>
                  <li v-if="page.isPublished">
                    <button
                      class="column-actions__action-menu__btn"
                      @click.stop="setPageBeingUnpublished(page)"
                    >
                      Despublicar página
                    </button>
                  </li>
                  <li
                    v-if="
                      page.language === 'pt' && !pageHasTranslation(page, 'en')
                    "
                  >
                    <button
                      class="column-actions__action-menu__btn"
                      @click.stop="addTranslation(page, 'en')"
                    >
                      Criar versão em inglês
                      <IconLanguage
                        language="en"
                        size="xs"
                        class="column-actions__action-menu__btn__flag"
                      />
                    </button>
                  </li>
                  <li
                    v-if="
                      page.language === 'pt' && !pageHasTranslation(page, 'es')
                    "
                  >
                    <button
                      class="column-actions__action-menu__btn"
                      @click.stop="addTranslation(page, 'es')"
                    >
                      Criar versão em espanhol
                      <IconLanguage
                        language="es"
                        size="xs"
                        class="column-actions__action-menu__btn__flag"
                      />
                    </button>
                  </li>
                  <li v-if="page.language === 'pt'">
                    <button
                      class="column-actions__action-menu__btn"
                      @click.stop="clonePage(page)"
                    >
                      Duplicar página
                    </button>
                  </li>
                  <li>
                    <button
                      class="column-actions__action-menu__btn"
                      @click.stop="editPage(page)"
                    >
                      Alterar nome e endereço
                    </button>
                  </li>
                  <li v-if="isGlobalUser && page.language === 'pt'">
                    <button
                      class="column-actions__action-menu__btn"
                      @click.stop="exportPage(page)"
                    >
                      Enviar para análise
                    </button>
                  </li>
                  <li>
                    <button
                      class="column-actions__action-menu__btn column-actions__action-menu__btn--remove"
                      @click.stop="pageBeingMovedToTrash = page"
                    >
                      {{
                        page.language === 'pt'
                          ? 'Mover para lixeira'
                          : 'Excluir permanentemente'
                      }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </CustomTable>
        <div class="d-flex">
          <router-link
            :to="{ name: 'trash' }"
            class="btn btn-white btn-icon go-trash"
          >
            <IconDelete size="xs" />
            <span>Páginas excluídas</span>
          </router-link>
        </div>
      </div>
      <div
        v-show="isModalOpen"
        class="pages__add-page"
        @click.self="resetPageEditing()"
      >
        <div class="pages__add-page__content">
          <div class="pages__add-page__content__header">
            <h2>{{ currentAction }} página</h2>
            <label
              for="btn-close"
              @click="resetPageEditing()"
            >
              <IconClose />
            </label>
          </div>
          <form
            class="pages__add-page__content__form"
            @submit.prevent="savePage"
          >
            <div class="pages__add-page__content__form__title">
              <label for="title">Nome</label>
              <input
                id="title"
                v-model.trim="pageTitle"
                type="text"
                autocomplete="off"
                placeholder="Escreva o nome"
                :class="{ 'is-invalid': hasError('title') }"
              />
              <p
                v-show="hasError('title')"
                class="invalid-feedback"
              >
                Por favor, preencha o nome da página
              </p>
            </div>
            <div class="pages__add-page__content__form__url-path">
              <label for="url-path">Endereço</label>
              <p>
                {{ baseUrlForPageBeingEdited
                }}<input
                  id="url-path"
                  v-model="maskedPageUrlPath"
                  v-imask="pageUrlPathMask"
                  type="text"
                  autocomplete="off"
                  placeholder="Escreva o endereço"
                  :class="{ 'is-invalid': hasError('url-path') }"
                  @accept="updatePageUrlPath"
                />
              </p>
              <p
                v-show="hasError('url-path')"
                class="invalid-feedback"
              >
                {{ urlPathErrorMessage }}
              </p>
            </div>

            <div class="pages__add-page__content__form__buttons">
              <button
                class="btn btn-secondary btn-white"
                type="button"
                @click="resetPageEditing()"
              >
                Cancelar
              </button>
              <button
                class="btn btn-secondary"
                type="submit"
              >
                {{ currentAction }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div
        v-show="isRemoveModalOpen"
        class="pages__remove-page-modal"
        @click.self="pageBeingMovedToTrash = null"
      >
        <div class="pages__remove-page-modal__content">
          <div class="pages__remove-page-modal__content__header">
            <label
              class="btn-close"
              @click="pageBeingMovedToTrash = null"
            >
              <IconClose />
            </label>
            <IconDelete
              class="text-danger"
              size="lg"
            />
            <h2>
              Tem certeza que <br />
              deseja
              {{
                pageBeingMovedToTrash && pageBeingMovedToTrash.language === 'pt'
                  ? 'mover para lixeira?'
                  : 'excluir permanentemente?'
              }}
            </h2>
          </div>

          <div class="pages__remove-page-modal__content__text">
            <p>
              {{
                pageBeingMovedToTrash && pageBeingMovedToTrash.language === 'pt'
                  ? 'A página será movida para a lixeira, mas ainda será possível restaurá-la em até 30 dias.'
                  : 'Ao excluir a página, todas as edições serão perdidas e não será possível reverter.'
              }}
              <br />
              <br />Você também pode redirecionar a URL da <br />
              página para outro local no
              <router-link
                id="go-to-settings"
                to="/settings#redirects"
              >
                Painel de Redirecionamentos </router-link
              >.
            </p>
          </div>

          <div class="pages__remove-page-modal__content__buttons">
            <button
              class="btn btn-neutral"
              type="button"
              @click="pageBeingMovedToTrash = null"
            >
              Voltar
            </button>
            <button
              class="btn btn-danger"
              type="submit"
              @click="movePageToTrash()"
            >
              {{
                pageBeingMovedToTrash && pageBeingMovedToTrash.language === 'pt'
                  ? 'Mover para lixeira'
                  : 'Excluir permanentemente'
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Modal "Despublicar" -->
      <div
        v-show="isUnpublishModalOpen"
        class="publish-page-modal"
        @click="setPageBeingUnpublished(null)"
      >
        <div
          class="publish-page-modal__content"
          @click.stop
        >
          <div class="publish-page-modal__content__header">
            <label
              class="btn-close"
              @click="setPageBeingUnpublished(null)"
            >
              <IconClose />
            </label>
            <IconWarning
              class="text-warning"
              size="lg"
            />
            <h2>Despublicar</h2>
          </div>

          <div class="publish-page-modal__content__text">
            <p>
              Ao <b>despublicar</b> sua landing, suas alterações não podem ser
              mais acessadas pelos seus usuários. Deseja continuar?
            </p>
          </div>

          <div class="publish-page-modal__content__buttons">
            <button
              class="btn btn-secondary btn-white"
              type="button"
              @click="setPageBeingUnpublished(null)"
            >
              Não, cancelar
            </button>
            <button
              class="btn btn-secondary"
              type="submit"
              @click="unpublishPage()"
            >
              Sim, quero despublicar!
            </button>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </main>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { IMaskDirective } from 'vue-imask';
import slugify from 'slugify';
import { Local, pt, Uppload, xhrUploader } from 'uppload';

import { pushDataLayer } from '@/helpers/gtm';
import isObject from '@/helpers/isObject';
import cloneObject from '@/helpers/cloneObject';
import getCreationDate from '@/helpers/getCreationDate';
import Toast from '@/helpers/Toast';
import getToken from '@/helpers/getToken';
import presets from '@/store/dicts/presets';
import isPresetEnabled from '@/store/helpers/isPresetEnabled';
import templates from '@/templates';
import IconStar from '@/components/icons/IconStar.vue';
import Header from '@/components/Header.vue';
import CustomTable from '@/components/CustomTable.vue';
import TemplateCard from '@/components/TemplateCard.vue';
import IconSettings from '@/components/icons/IconSettings.vue';
import IconDotsVert from '@/components/icons/IconDotsVert.vue';
import IconLanguage from '@/components/icons/IconLanguage.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconWarning from '@/components/icons/IconWarning.vue';
import IconDelete from '@/components/icons/IconDelete.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import IconSearch from '@/components/icons/IconSearch.vue';
import IconUpload from '@/components/icons/IconUpload.vue';
import Footer from '@/components/Footer.vue';
import RadialProgressBar from '@/components/RadialProgressBar.vue';

export default {
  name: 'PageList',
  components: {
    Header,
    Footer,
    CustomTable,
    IconSettings,
    IconClose,
    IconWarning,
    IconDelete,
    IconDotsVert,
    IconLanguage,
    IconPlus,
    IconStar,
    IconSearch,
    IconUpload,
    TemplateCard,
    RadialProgressBar,
  },
  directives: {
    imask: IMaskDirective,
    positioned: {
      bind(el, binding, vnode) {
        /* eslint-disable no-param-reassign */
        el.positionByViewport = () => {
          const parentPosition = el.parentElement.getBoundingClientRect();
          const viewportHeight =
            window.innerHeight || document.documentElement.clientHeight;
          if (parentPosition.bottom <= viewportHeight - 200) {
            el.style.top = '100%';
            el.style.bottom = 'unset';
          } else {
            el.style.bottom = '100%';
            el.style.top = 'unset';
          }
        };

        vnode.context.$nextTick(el.positionByViewport);
        window.addEventListener('scroll', el.positionByViewport);
      },
      unbind(el) {
        window.removeEventListener('scroll', el.positionByViewport);
      },
    },
    // clickaway taken from https://stackoverflow.com/questions/36170425/detect-click-outside-element
    clickaway: {
      bind(el, binding, vnode) {
        // eslint-disable-next-line no-param-reassign
        el.clickOutsideEvent = (event) => {
          // here I check that click was outside the el and his children
          if (!(el === event.target || el.contains(event.target))) {
            // and if it did, call method provided in attribute value
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },
  data() {
    return {
      pageBeingMovedToTrash: null,
      pageBeingEdited: null,
      pageBeingUnpublished: null,
      pageLanguage: 'pt',
      pageTitle: '',
      apiPageUrlPath: '_', // refer to pageUrlPath to get some context
      searchTerm: '',
      isModalOpen: false,
      openedActionMenu: null,
      fieldsWithError: [],
      urlPathErrorMessage: '',
      showTemplates: false,
      selectedTemplate: null,
      pageUrlPathMask: {
        mask: /^[a-zA-Z0-9-/]+$/,
        prepare(url) {
          return url
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s/g, '-')
            .toLowerCase();
        },
      },
      toast: null,
      uploader: null,
    };
  },
  computed: {
    templateOptions() {
      const newFirst = this.newTemplatesFirst(templates);
      const filteredTemplates = newFirst.filter((template) =>
        Object.values(template.body.instances).every((instance) => {
          const thisPreset = presets.find(
            (preset) => preset.module === instance.module,
          );

          if (!thisPreset) {
            return true;
          }

          // every instance in the template must be enabled
          return isPresetEnabled(
            thisPreset,
            this.$store.state,
            this.$store.getters,
          );
        }),
      );

      return filteredTemplates;
    },
    // We have this computed to replace empty URL paths by an underscore and
    // vice versa, since Editor API does not accept real empty URL paths.
    pageUrlPath: {
      get() {
        return this.getPageRealUrlPath(this.apiPageUrlPath);
      },
      set(pageUrlPath) {
        if (/\S/.test(pageUrlPath)) {
          this.apiPageUrlPath = pageUrlPath;
        } else {
          this.apiPageUrlPath = '_';
        }
      },
    },
    maskedPageUrlPath: {
      get() {
        return this.pageUrlPath;
      },
      set() {
        // Do nothing here. Mask's accept event will do the job
      },
    },
    parentPageList() {
      return this.parentPageList;
    },
    currentAction() {
      return this.pageBeingEdited === null ? 'Criar' : 'Alterar';
    },
    baseUrlForPageBeingEdited() {
      return this.getPageBaseUrl(this.pageBeingEdited);
    },
    isRemoveModalOpen() {
      return this.pageBeingMovedToTrash !== null;
    },
    isUnpublishModalOpen() {
      return this.pageBeingUnpublished !== null;
    },
    isValid() {
      return this.fieldsWithError.length === 0;
    },
    isValidRepeatedUrl() {
      if (
        !!this.pageBeingEdited &&
        this.pageBeingEdited.urlPath === this.apiPageUrlPath &&
        this.pageBeingEdited.language === this.pageLanguage
      ) {
        return false;
      }

      return this.urlPathExists(this.apiPageUrlPath, this.pageLanguage);
    },
    foundOutPages() {
      return this.parentPageList.filter((pageId) => this.isPageMatch(pageId));
    },
    pageList() {
      return this.searchTerm.length > 0
        ? this.foundOutPages
        : this.parentPageList;
    },
    tableHeaders() {
      const headers = [
        {
          name: 'title',
          label: 'Nome',
        },
        {
          name: 'seo',
          label: 'SEO',
          maxWidth: '120px',
        },
        {
          name: 'status',
          label: 'Status',
          maxWidth: '120px',
        },
        {
          name: 'createdAt',
          label: 'Criado em',
          maxWidth: '150px',
          type: 'date',
        },
        {
          name: 'actions',
          label: 'Ações',
          maxWidth: '200px',
          isSortable: false,
        },
      ];

      return headers;
    },
    listForTable() {
      return this.pageList.map((pageId) => {
        const currentPage = this.pages[pageId];
        const page = {
          seo: currentPage.seo,
          status: this.getPageStatus(currentPage),
          createdAt: getCreationDate(currentPage.id),
          children: [],
          ...currentPage,
        };
        const hasTranslation = Object.keys(page.translationIds).length;

        if (hasTranslation) {
          page.translationIds.forEach((translationId) => {
            const currentTranslationPage = this.pages[translationId];
            page.children.push({
              status: this.getPageStatus(currentTranslationPage),
              createdAt: getCreationDate(currentTranslationPage.id),
              ...currentTranslationPage,
            });
          });
        }

        return page;
      });
    },
    ...mapState(['customer', 'pages', 'parentPageList', 'loading']),
    ...mapGetters([
      'urlPathExists',
      'siteHostname',
      'isGlobalUser',
      'isViewerUser',
      'isAdminUser',
    ]),
  },
  watch: {
    searchTerm() {
      this.$refs.customTable.$emit('search');
    },
  },
  mounted() {
    this.toast = new Toast({
      customClass: {
        container: 'export',
      },
      position: 'top-end',
    });
  },
  methods: {
    newSearch() {
      this.searchTerm = '';
      this.$refs.search__field.focus();
    },
    isPageMatch(pageId) {
      const term = slugify(this.searchTerm, { lower: true, strict: true });
      const { title, urlPath, translationIds } = this.pages[pageId];

      const pageString = [
        title,
        urlPath,
        this.getPageStatus(this.pages[pageId]),
        this.getPageAbsoluteUrl(this.pages[pageId]),
      ].join(' ');

      const pageSlug = slugify(pageString, { lower: true, strict: true });

      return (
        pageSlug.includes(term) ||
        (translationIds && translationIds.some(this.isPageMatch))
      );
    },
    updatePageUrlPath(event) {
      this.pageUrlPath = event.detail.value;
    },
    getPageBaseUrl(page, protocol = true) {
      let pageBaseUrl = `${this.siteHostname}/`;

      if (protocol === true) {
        pageBaseUrl = `https://${pageBaseUrl}`;
      }

      if (isObject(page) && page.language !== 'pt') {
        pageBaseUrl += `${page.language}/`;
      }

      return pageBaseUrl;
    },
    getPageRealUrlPath(urlPath) {
      if (urlPath === '_') {
        return '';
      }

      return urlPath;
    },
    getPageAbsoluteUrl(page) {
      let absolutePageUrl = this.getPageBaseUrl(page);

      absolutePageUrl += this.getPageRealUrlPath(page.urlPath);

      return absolutePageUrl;
    },
    validate() {
      const urlPathValidator = /^(?:_|(?:[a-z0-9-]+(?:\/[a-z0-9-]+)*))$/;

      this.fieldsWithError = [];

      if (!/\S/.test(this.pageTitle)) {
        this.fieldsWithError.push('title');
      }

      if (!urlPathValidator.test(this.apiPageUrlPath)) {
        this.urlPathErrorMessage = 'Ops! O endereço digitado não é válido';

        this.fieldsWithError.push('url-path');
      } else if (this.isValidRepeatedUrl) {
        this.urlPathErrorMessage =
          'Ops! Já existe uma página com esse endereço';

        this.fieldsWithError.push('url-path');
      } else if (this.apiPageUrlPath === 'healthz') {
        this.urlPathErrorMessage = 'Ops! Esse endereço é usado pelo Editor';

        this.fieldsWithError.push('url-path');
      }
    },
    savePage() {
      this.validate();

      if (this.isValid) {
        const page = {
          title: this.pageTitle,
          urlPath: this.apiPageUrlPath,
        };

        if (this.selectedTemplate) {
          Object.assign(page, cloneObject(this.selectedTemplate.body));
        }

        if (this.pageBeingEdited === null) {
          this.$store.dispatch('addPage', page).then(() => {
            this.goToEditor(page);
          });

          pushDataLayer({
            event: 'page-created',
            template: this.selectedTemplate.name,
          });
        } else {
          const { redirect, ...pageBeingEdited } = this.pageBeingEdited;

          this.$store
            .dispatch('editPageBasicInfo', {
              page: pageBeingEdited,
              newBasicInfo: page,
            })
            .then(() => {
              if (redirect) {
                this.goToEditor({ ...pageBeingEdited, ...page });
              }
            });

          this.toggleActionMenu(null);
        }

        this.resetPageEditing();
      }
    },
    resetPageEditing() {
      this.pageBeingEdited = null;
      this.pageLanguage = 'pt';
      this.pageTitle = '';
      this.pageUrlPath = '';
      this.isModalOpen = false;
      this.fieldsWithError = [];
      this.selectedTemplate = null;
    },
    editPage(page) {
      this.pageBeingEdited = page;
      this.$nextTick(() => {
        this.pageUrlPath = page.urlPath;
        this.pageLanguage = page.language;
        this.pageTitle = page.title;
        this.isModalOpen = true;
      });
    },
    fireToast(message) {
      this.toast.fire({
        showCloseButton: true,
        background: '#FFFFFF',
        html: `
            <style>
              .export {
                z-index: 2001;
              }
              .export .swal2-html-container {
                padding: 0 !important;
              }
              .export .toast-contents {
                padding-right: 16px;
                display: flex;
                align-items: center;
              }
              .export .toast-contents p {
                font-size: 16px;
              }
              .export .toast-svg {
                padding-right: 16px;
              }
              .export .swal2-close {
                position: absolute !important;
                font-size: 40px;
              }
              </style>
              <div class="toast-contents">
                <div class="toast-svg">
                  <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 0.6875C5.97676 0.6875 0.6875 5.97676 0.6875 12.5C0.6875 19.0232 5.97676 24.3125 12.5 24.3125C19.0232 24.3125 24.3125 19.0232 24.3125 12.5C24.3125 5.97676 19.0232 0.6875 12.5 0.6875ZM17.6021 8.64248L12.0491 16.3417C11.9715 16.45 11.8692 16.5383 11.7507 16.5992C11.6321 16.6601 11.5008 16.6918 11.3675 16.6918C11.2343 16.6918 11.1029 16.6601 10.9844 16.5992C10.8659 16.5383 10.7635 16.45 10.6859 16.3417L7.39795 11.7854C7.29775 11.6457 7.39795 11.4506 7.56934 11.4506H8.80596C9.0749 11.4506 9.33066 11.5798 9.48887 11.8013L11.3662 14.4063L15.5111 8.6583C15.6693 8.43945 15.9225 8.30762 16.194 8.30762H17.4307C17.6021 8.30762 17.7022 8.50273 17.6021 8.64248V8.64248Z" fill="#7CC970"/>
                  </svg>
                </div>
                <div>
                  <p>${message}</p>
                </div>
              </div>`,
      });
    },
    exportPage(page) {
      this.$store.dispatch('exportPage', page).then(() => {
        this.fireToast('Página enviada ao suporte para análise!');
      });
    },
    importPage() {
      const maxFileSize = 200 * 1024; // 200kb, limite definido na api

      if (!this.uploader) {
        const baseUrl =
          process.env.VUE_APP_API_BASE_URL ||
          'https://api.siteletsbook.com.br/v2/';
        const endpoint = `${baseUrl}pages/import`;

        this.uploader = new Uppload({
          lang: pt,
          uploader: xhrUploader({
            endpoint,
            fileKeyName: 'file',
            settingsFunction(xmlHttp) {
              xmlHttp.setRequestHeader('Authorization', getToken());
            },
          }),
        });

        this.uploader.use(
          new Local({
            maxFileSize,
            mimeTypes: ['application/json'],
          }),
        );

        this.uploader.on('upload', () => {
          this.loadPages();
          this.fireToast('Página importada com sucesso!');
        });
      }

      const lang = {
        ...pt,
        errors: {
          ...pt.errors,
          file_too_large: `Seu arquivo deve ter até ${maxFileSize} KB`,
        },
      };

      this.uploader.updateSettings({ lang });
      this.uploader.open();
    },
    movePageToTrash() {
      this.$store
        .dispatch('movePageToTrash', this.pageBeingMovedToTrash)
        .then(() => {
          this.pageBeingMovedToTrash = null;

          this.toggleActionMenu(null);
        });
    },
    goToEditor(page) {
      this.$router.push({ name: 'editor', params: page });
    },
    setFavorite(page) {
      this.$store.dispatch('editPageBasicInfo', {
        page,
        newBasicInfo: { isFavorite: !page.isFavorite },
      });
      this.toggleActionMenu(null);
    },
    toggleActionMenu(pageId) {
      if (this.openedActionMenu === pageId) {
        this.openedActionMenu = null;
      } else {
        this.openedActionMenu = pageId;
      }
    },
    closeActionMenu() {
      this.openedActionMenu = null;
    },
    hasError(field) {
      return this.fieldsWithError.includes(field);
    },
    addTranslation(page, language) {
      this.createPageTranslation({
        page,
        language,
      }).then((newPage) => {
        this.toggleActionMenu(null);
        this.editPage({ ...newPage, redirect: true });
      });
    },
    clonePage(page) {
      this.$store.dispatch('clonePage', page).then((newPage) => {
        this.toggleActionMenu(null);
        this.editPage({ ...newPage, redirect: true });
      });
    },
    setPageBeingUnpublished(pageBeingUnpublished) {
      this.pageBeingUnpublished = pageBeingUnpublished;
    },
    unpublishPage() {
      this.setPageStatus({
        page: this.pageBeingUnpublished,
        isPublished: false,
      }).then(() => {
        this.setPageBeingUnpublished(null);
        this.toggleActionMenu(null);
      });
    },
    isDirtyPage(page) {
      // Handle legacy
      if (typeof page.isDirty !== 'boolean') {
        return !page.isPublished;
      }

      return page.isDirty;
    },
    getPageStatus(page) {
      if (!page.isPublished) {
        return 'Rascunho';
      }

      if (this.isDirtyPage(page)) {
        return 'Alterada';
      }

      return 'Publicada';
    },
    getStatusHelpText(page) {
      if (!page.isPublished) {
        return 'Página não publicada';
      }

      if (this.isDirtyPage(page)) {
        return 'Alterações não publicadas';
      }

      return 'Página publicada';
    },
    pageHasTranslation(page, language) {
      return (
        Array.isArray(page.translations) && page.translations.includes(language)
      );
    },
    newTemplatesFirst(templateList) {
      return templateList.sort((itemA, itemB) => {
        if (itemB.name === 'Em branco') {
          return 1;
        }

        if (!!itemA.isNew === !!itemB.isNew) {
          return 0;
        }

        return itemB.isNew ? 1 : -1;
      });
    },
    toggleTemplates() {
      this.showTemplates = !this.showTemplates;
    },
    selectTemplate(template) {
      this.selectedTemplate = template;

      if (template.name === 'Pré Check-in') {
        this.pageUrlPath = 'pre-checkin';
      }

      this.isModalOpen = true;
    },
    seoTitleText(seo) {
      return seo === 1
        ? 'Sua página está com SEO completo!'
        : 'Sua página precisa melhorar. \nPreencha os campos de SEO.';
    },
    ...mapActions(['createPageTranslation', 'setPageStatus', 'loadPages']),
  },
};
</script>

<style scoped lang="scss">
main {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f2f2f2;
  margin: 0;
  padding-bottom: 45px; // footer height

  &.loading {
    opacity: 0.75;
    cursor: progress;

    > * {
      pointer-events: none;
    }
  }

  header {
    position: unset;
    width: unset;
  }
}

.pages {
  width: 100%;
  max-width: 1600px;
  flex-grow: 1;
  margin: 0 auto;
  padding: 32px 16px 24px;

  &__management {
    padding: 32px;
    background-color: $lb-white;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      .search {
        position: relative;
        width: 100%;
        max-width: 450px;
        margin-left: auto;

        i {
          position: absolute;
          top: 50%;
          right: 12px;
          transform: translateY(-50%);
        }

        &__field {
          height: 40px;
          background: none;
          padding-right: 25px;
          border-radius: 100px;
          border: 1px solid $lb-gray;
          padding: 4px 36px 4px 16px;

          &::placeholder {
            color: #949494;
          }
        }
      }

      .btn {
        height: 40px;
        padding: 4px 20px;
        margin-left: 24px;
        font-size: $size-small;
      }
    }

    h1 {
      font-size: $size-bigger;
      font-weight: $lb-medium;
    }

    .table-container {
      margin-bottom: 32px;
    }

    .btn.go-trash {
      padding-left: 45px;
      padding-right: 45px;
    }
  }

  &__empty-state {
    height: 350px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: $size-normal;

    button {
      margin-top: 20px;
    }
  }

  &__table {
    margin-top: 20px;

    ::v-deep {
      th[data-identifier='actions'],
      th[data-identifier='createdAt'],
      td[data-identifier='createdAt'],
      td[data-identifier='actions'] {
        justify-content: center;
      }
    }

    .column-title {
      display: flex;
      align-items: center;
      width: 100%;
      cursor: pointer;

      .favorite-icon {
        outline: none;
        padding: 4px;

        ::v-deep {
          svg {
            fill: #e4e4e4;
          }

          .active svg {
            fill: #fa8425;
          }
        }

        &:not(:disabled):hover {
          ::v-deep {
            svg {
              fill: $gray-10 !important;
            }

            .active svg {
              fill: #fac325 !important;
            }
          }
        }
      }

      .icon {
        margin-right: 12px;
      }

      .icon,
      .favorite-icon {
        flex-shrink: 0;
      }
    }

    .column-status {
      display: flex;
      align-items: center;
      justify-content: center;

      .page-status {
        &::before {
          border-radius: 1986px;
          content: '';
          display: inline-block;
          height: 8px;
          margin-right: 8px;
          width: 8px;
        }

        &--draft {
          &::before {
            background-color: #fca35b;
          }
        }

        &--published {
          &::before {
            background-color: #58f6a3;
          }
        }

        &--dirty {
          &::before {
            background-color: #7590f4;
          }
        }
      }
    }

    .column-actions {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;

      > * + * {
        margin-left: 8px;
      }

      .icon {
        color: $crm-blue-primary;
        padding: 4px;
      }

      button[disabled] {
        opacity: 0.5;
      }

      &__cta {
        color: $crm-blue-primary;
        font-weight: $lb-medium;
        padding: 6px;

        &:hover {
          text-decoration: underline;
        }

        &--disabled {
          text-decoration: none;
          color: #7f7f7f;
          &:hover {
            text-decoration: none;
          }
        }
      }

      &__cta-menu {
        border-radius: 50%;
        outline: none;

        &:hover {
          background: #d1dafb;
        }
      }

      &__action-menu {
        background-color: $lb-white;
        box-shadow: 4px 4px 25px rgba(0, 0, 0, 0.25);
        position: absolute;
        right: 0;
        z-index: 1;

        &__btn {
          white-space: nowrap;
          outline: none;
          border: none;
          background: none;
          color: inherit;
          padding: 0.5em 1.5em;
          width: 100%;
          text-align: left;

          &--goto {
            padding-top: 1.5em;
          }

          &--remove {
            color: $crm-red-primary;
            padding-bottom: 1.5em;
          }

          &:hover {
            background-color: #f5f5f5;
            cursor: pointer;
          }
        }
      }
    }
  }

  &__add-page {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;

    &__content {
      height: 400px;
      width: 545px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;

      &__header {
        display: flex;
        justify-content: space-between;
        width: 100%;

        h2 {
          font-size: $size-bigger;
        }

        label {
          height: 26px;
          width: 26px;
          display: flex;
          justify-content: center;
          align-items: center;
          margin: -16px -10px -20px 0px;
          cursor: pointer;
        }
      }

      &__form {
        width: 100%;
        height: 100%;
        padding: 20px 0px 20px 0px;
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .invalid-feedback {
          display: block;
        }

        input {
          border: none;
          border-bottom: 1px solid $lb-dark-gray;
          outline: none;
          padding-bottom: 6px;

          &:invalid,
          &.is-invalid {
            border-bottom: 2px solid #e0414b;
            box-shadow: none;
          }
        }

        &__title {
          display: flex;
          flex-direction: column;
          margin-bottom: -30px;

          .invalid-feedback {
            margin-bottom: 35px;
          }
        }

        &__url-path {
          display: flex;
          flex-direction: column;

          p {
            display: flex;
            align-items: center;

            input {
              margin-left: 14px;
              width: 100%;
            }
          }
        }

        &__buttons {
          display: flex;
          justify-content: space-between;

          button {
            min-width: 0px;
          }

          .btn-secondary {
            width: 40%;
          }

          .btn-white {
            width: 30%;
          }
        }
      }
    }
  }

  &__remove-page-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;

    &__content {
      width: 545px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;
      align-items: center;

      &__header {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        flex-direction: column;
        position: relative;

        h2 {
          font-size: $size-bigger;
          font-weight: $lb-medium;
          line-height: 35px;
          text-align: center;
          letter-spacing: -1px;
          color: $gray-900;
          margin: 40px 0 20px 0;
        }

        .btn-close {
          position: absolute;
          top: 0;
          right: 0;
          cursor: pointer;
        }
      }

      &__text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;

        p {
          font-size: $size-normal;
          line-height: 26px;
          color: $gray-900;
          margin-bottom: 20px;
        }
      }

      &__buttons {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        width: 70%;
      }
    }
  }

  &__templates {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 1080px;
    margin: 0 auto;
    margin-bottom: calc(40px - 1em);
  }

  .loader {
    width: 100%;
  }
}

footer {
  position: fixed;
  bottom: 0;
  left: 0;
}

.publish-page-modal {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: rgba($lb-dark-gray, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9;

  &__content {
    width: 545px;
    display: flex;
    padding: 40px;
    flex-direction: column;
    background-color: $lb-white;
    align-items: center;

    &__header {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      flex-direction: column;
      position: relative;

      h2 {
        font-size: $size-bigger;
        font-weight: $lb-medium;
        line-height: 35px;
        text-align: center;
        letter-spacing: -1px;
        color: $gray-900;
        margin: 40px 0 20px 0;
      }

      .btn-close {
        position: absolute;
        top: 0;
        right: 0;
        cursor: pointer;
      }
    }

    &__text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      line-height: 26px;
      color: $gray-900;
      margin-bottom: 20px;

      &__no-errors {
        font-size: $size-normal;
      }

      &__has-errors {
        font-size: $size-small;
      }
    }

    &__buttons {
      display: flex;
      justify-content: space-evenly;
      align-items: center;
      width: 70%;
    }
  }
}
</style>
